import { CheckCircleFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Modal, Spin, Tag, Typography } from "antd";
import styled from "@emotion/styled";
import ManageParcelPageHeader from "components/ManageParcel/ParcelShared/ManageParcelPageHeader";
import { useFactoryCodCreditInfo } from "hooks/useFactoryCredit";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditPackage, getFactoryCreditPackages } from "services/factoryCreditApi";
import { PaymentChannelIcon } from "./creditTopupIcons";

const { Text } = Typography;

const numberFormatTHB = (n: number) => n.toLocaleString("th-TH", { minimumFractionDigits: 0 });

type ChannelOption = { key: string; label: string; enabled: boolean };
// 2026-09-07: ตามที่ขอ เหลือช่องทางชำระเงินแค่พร้อมเพย์ (ช่องอื่นที่เคยโชว์เป็น "เร็วๆ นี้" เอาออกไปก่อน —
// initiateFactoryCreditTopup ฝั่ง API ก็รองรับจริงแค่พร้อมเพย์ตัวเดียวอยู่แล้วด้วย)
const PAYMENT_CHANNELS: ChannelOption[] = [
  { key: "promptpay", label: "พร้อมเพย์ (สแกน QR)", enabled: true },
];

/**
 * การ์ดเลือกแพ็กเกจ — ดีไซน์ให้ตรงกับ bangbow-admin (TopUpForm.tsx) เป๊ะ (2026-09-03: ปรับสีจากฟ้า
 * ant-design #1677ff เดิม เป็นม่วง #666CFF ของ admin ตรงๆ ทั้งสี border/shadow/ตัวเลข + เพิ่ม hover
 * effect (border/shadow เด่นขึ้น + ยกขึ้นเล็กน้อย translateY(-1px)) ที่ต้องใช้ styled-component แทน
 * inline style เพราะ :hover ทำผ่าน style object ตรงๆ ไม่ได้)
 */
const PackageCardWrap = styled.div<{ $selected: boolean }>`
  position: relative;
  border: 2px solid ${(p) => (p.$selected ? "#666CFF" : "rgba(0, 0, 0, 0.12)")};
  border-radius: 12px;
  padding: 20px 24px;
  min-width: 160px;
  cursor: pointer;
  background-color: ${(p) => (p.$selected ? "rgba(102, 108, 255, 0.07)" : "#fff")};
  box-shadow: ${(p) =>
    p.$selected
      ? "0 0 0 3px rgba(102, 108, 255, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)"
      : "0 1px 4px rgba(0, 0, 0, 0.06)"};
  transition: all 0.2s;

  &:hover {
    border-color: #666cff;
    box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.12), 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

/**
 * 2026-09-03 (responsive): จอปกติ = ซ้าย 60% (การ์ดแพ็กเกจ) / ขวา 40% (สรุป+ช่องทางชำระเงิน) เรียงข้างกัน
 * จอเล็ก (≤900px) = สลับเป็นคอลัมน์เดียว ซ้อนกันแนวตั้ง — การ์ดแพ็กเกจอยู่บน (มาก่อนใน DOM อยู่แล้ว) การ์ด
 * ขวาตกลงมาอยู่ล่างเอง ไม่ต้องสลับลำดับ element ใดๆ แค่เปลี่ยน grid-template-columns เป็นคอลัมน์เดียว
 */
const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;
  align-items: flex-start;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * 2026-09-03 (responsive): ใช้ auto-fill แทน repeat(4, ...) ตายตัว — จอกว้างพอ (เดสก์ท็อป, container
 * นี้ถูกขยายเป็น maxWidth 1400 แล้ว) คำนวณได้พอดี 4 คอลัมน์เหมือน admin เป๊ะอยู่แล้วโดยไม่ต้องล็อกจำนวน
 * คอลัมน์เอง — พอจอแคบลง (จอเล็ก หรือคอลัมน์ซ้ายถูกบีบแคบตอน SplitLayout ยุบเป็นคอลัมน์เดียวที่จอเล็ก)
 * จำนวนคอลัมน์ต่อแถวจะลดลงเองอัตโนมัติ (2-3 คอลัมน์) แล้วการ์ดที่เหลือ "ตกลงมาอีกบรรทัดต่อเนื่องกัน"
 * ตามที่ขอ ไม่มีทางล้น container เพราะ auto-fill ไม่มีทางสร้างคอลัมน์เกินกว่าที่ minmax กำหนดพอดี
 */
const PackageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`;

const PackageCard: FC<{ pkg: CreditPackage; multiplier: number; selected: boolean; onClick: () => void }> = ({
  pkg,
  multiplier,
  selected,
  onClick,
}) => (
  <PackageCardWrap onClick={onClick} $selected={selected}>
    <div style={{ textAlign: "right" }}>
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          lineHeight: 1.2,
          color: selected ? "#666CFF" : "rgba(0,0,0,0.85)",
        }}
      >
        {numberFormatTHB(pkg.credit_amount * multiplier)}
      </div>
      <div style={{ fontSize: 11, letterSpacing: 0.5, color: "rgba(0,0,0,0.65)" }}>Credits</div>
    </div>
    <div
      style={{
        borderTop: `1px solid ${selected ? "rgba(102, 108, 255, 0.3)" : "#f0f0f0"}`,
        margin: "12px 0",
      }}
    />
    <div style={{ textAlign: "right", fontWeight: 700 }}>
      {Number(pkg.price_thb).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
    </div>
    <div style={{ textAlign: "right", fontSize: 12, color: "#ff4d4f", marginTop: 4 }}>
      หมดอายุใน {pkg.expiry_days} วัน
    </div>
  </PackageCardWrap>
);

/**
 * หน้าเติมเครดิตเองผ่าน payment gateway (Ksher — "Phase L") — เป็นหน้าเต็ม เรียกได้ทั้งจาก "จัดการเครดิต"
 * และ "ส่งพัสดุนอก SKU"
 *
 * เลย์เอาต์ (2026-09-01, ตามที่ขอ — แบบเดียวกับ bangbow-admin): ซ้าย-ขวา
 *   ซ้าย  = การ์ดแพ็กเกจให้เลือก
 *   ขวาบน = สรุปแพ็กเกจที่เลือก + ราคาสุทธิที่ต้องจ่าย
 *   ขวาล่าง = ช่องทางชำระเงิน + ปุ่มยืนยันการชำระเงิน
 * กด "ยืนยันคำสั่งซื้อ" → modal สรุปรายการ → กด "ยืนยันการชำระเงิน" → 2026-09-08: navigate ไปหน้า
 * checkout เต็มหน้า (CreditTopupCheckoutPage.tsx, /manage-credit/topup/checkout?packageVersionId=...)
 * แทนการเปิด PaymentQrModal แบบเดิม (modal นั้นเลิกใช้แล้ว — ดูดีไซน์ใหม่ที่ CreditTopupCheckoutPage.tsx)
 *
 * ช่องทางชำระเงินตอนนี้ใช้ได้จริงแค่พร้อมเพย์ (ตัวเดียวที่ initiateFactoryCreditTopup รองรับฝั่ง API
 * ตอนนี้) ช่องอื่นที่ enum ฝั่ง backend เตรียมไว้แล้ว (บัตร/TrueMoney/Alipay) โชว์เป็น "เร็วๆ นี้" ไปก่อน
 * ไม่ให้เข้าใจผิดว่าใช้ได้จริง
 */
const CreditTopupPage: FC = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [channel, setChannel] = useState("promptpay");
  // 2026-09-04: ต่อระบบชำระเงินจริงแล้ว — กด "ยืนยันคำสั่งซื้อ" เปิด modal สรุปรายการก่อน (confirmModalOpen)
  // 2026-09-08: กด "ยืนยันการชำระเงิน" ในนั้น navigate ไปหน้า checkout เต็มหน้าแทน (ไม่เปิด PaymentQrModal
  // แบบเดิมแล้ว — ดู CreditTopupCheckoutPage.tsx)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { data: codCreditInfo } = useFactoryCodCreditInfo();
  const multiplier = codCreditInfo?.cod_credit_multiplier ?? 2;

  const { data: packages, isLoading: loadingPackages } = useQuery<CreditPackage[], Error>({
    queryKey: ["factoryCreditPackagesForTopup"],
    queryFn: getFactoryCreditPackages,
    staleTime: 60_000,
  });

  const handleBack = () => {
    if (window.history.state && typeof window.history.state.idx === "number" && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/manage-credit");
  };

  const renderForm = () => (
    // 2026-09-01: แยกออกจากการ์ด "เติมเครดิต" ด้านบน (ชื่อซ้ำกับ ManageParcelPageHeader อยู่แล้วด้วย) —
    // ด้านล่างเป็นการ์ด 2 ฝั่งซ้าย-ขวาเดี่ยวๆ ระดับเดียวกัน ไม่ซ้อนอยู่ในการ์ดใหญ่อีกชั้นแล้ว
    // 60/40 ซ้าย-ขวา (จอเล็ก ≤900px ยุบเป็นคอลัมน์เดียว — ดู docstring ของ SplitLayout)
    <SplitLayout>
      {/* ซ้าย — การ์ดแพ็กเกจ (60% / เต็มความกว้างตอนจอเล็ก) */}
      <Card title="เลือกแพ็กเกจเครดิต" style={{ borderRadius: 12, minWidth: 0 }}>
        {/* 2026-09-03: .ant-card-body ถูก override เป็น padding: 0 !important ทั้งแอป (ดู main.css) —
         * เดิมการ์ด package เลยชิดขอบการ์ดนอกเกินไป ต้องเติม padding เองในนี้เสมอ (พึ่ง Card เองไม่ได้)
         * ใช้ 24px = ค่า default ของ .ant-card-body ตอนยังไม่ถูก override ให้ดูเป็นธรรมชาติเหมือนของ
         * admin (TopUpForm.tsx) ที่มีระยะห่างรอบ card เลือกแพ็กเกจปกติ */}
        <div style={{ padding: 24 }}>
          {loadingPackages && (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin />
            </div>
          )}
          {!loadingPackages && !packages?.length && (
            <Alert type="warning" message="ยังไม่มีแพ็กเกจเครดิตให้เลือกในขณะนี้ กรุณาติดต่อแอดมิน" showIcon />
          )}
          {!!packages?.length && (
            <PackageGrid>
              {packages.map((pkg) => (
                <PackageCard
                  key={pkg.version_id}
                  pkg={pkg}
                  multiplier={multiplier}
                  selected={selectedPackage?.version_id === pkg.version_id}
                  onClick={() => setSelectedPackage(pkg)}
                />
              ))}
            </PackageGrid>
          )}
        </div>
      </Card>

      {/* ขวา (40%) — บน: สรุป/ราคาสุทธิ, ล่าง: ช่องทางชำระเงิน + ปุ่มยืนยัน */}
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* 2026-09-03: .ant-card-body ถูก override เป็น padding: 0 !important ทั้งแอป (ดู main.css) —
         * เนื้อหาเลยชิดเส้นขอบการ์ดเกินไปเหมือนการ์ด "เลือกแพ็กเกจเครดิต" ด้านซ้าย เติม padding เองด้วย
         * div ครอบเหมือนกัน (24px = ค่า default ของ .ant-card-body ตอนยังไม่ถูก override) */}
        <Card title="รายละเอียดคำสั่งซื้อ" style={{ borderRadius: 12 }}>
          <div style={{ padding: 24 }}>
            {selectedPackage ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text style={{ color: "rgba(0,0,0,0.85)", fontSize: 15 }}>เครดิตที่จะได้รับ</Text>
                  <Text strong>{numberFormatTHB(selectedPackage.credit_amount * multiplier)} เครดิต</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <Text style={{ color: "rgba(0,0,0,0.85)", fontSize: 15 }}>อายุการใช้งาน</Text>
                  <div style={{ textAlign: "right" }}>
                    <Text strong style={{ display: "block" }}>{selectedPackage.expiry_days} วัน</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (หลังจากชำระเงินสำเร็จ)
                    </Text>
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #f0f0f0", margin: "12px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: 15 }}>
                    ราคาสุทธิที่ต้องจ่าย
                  </Text>
                  <Text strong style={{ fontSize: 22, color: "#1677ff" }}>
                    {Number(selectedPackage.price_thb).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                  </Text>
                </div>
              </>
            ) : (
              <Text type="secondary">กรุณาเลือกแพ็กเกจด้านซ้ายก่อน</Text>
            )}
          </div>
        </Card>

        <Card title="ช่องทางชำระเงิน" style={{ borderRadius: 12 }}>
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {PAYMENT_CHANNELS.map((c) => {
                const active = channel === c.key;
                return (
                  <div
                    key={c.key}
                    onClick={() => c.enabled && setChannel(c.key)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: `1px solid ${active ? "#1677ff" : "#e6e6e6"}`,
                      borderRadius: 8,
                      padding: "10px 14px",
                      cursor: c.enabled ? "pointer" : "not-allowed",
                      backgroundColor: active ? "rgba(22, 119, 255, 0.06)" : "#fff",
                      opacity: c.enabled ? 1 : 0.5,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <PaymentChannelIcon
                        type={c.key}
                        style={{ fontSize: 18, color: active ? "#1677ff" : "rgba(0,0,0,0.5)" }}
                      />
                      <Text strong={active}>{c.label}</Text>
                    </span>
                    {!c.enabled && <Tag>เร็วๆ นี้</Tag>}
                    {c.enabled && active && <CheckCircleFilled style={{ color: "#1677ff" }} />}
                  </div>
                );
              })}
            </div>
            <Button
              type="primary"
              block
              size="large"
              disabled={!selectedPackage}
              onClick={() => setConfirmModalOpen(true)}
            >
              ยืนยันคำสั่งซื้อ
            </Button>
          </div>
        </Card>
      </div>
    </SplitLayout>
  );

  return (
    <>
      {/* การ์ดหัวข้อ/ปุ่มย้อนกลับ — แยกจากการ์ดเนื้อหาด้านล่างเป็นคนละการ์ดเลย */}
      <Card bordered={false} className="criclebox h-full tabs-mobile" style={{ marginBottom: 16 }}>
        <ManageParcelPageHeader title="เติมเครดิต" onBack={handleBack} />
      </Card>

      {/* การ์ดเนื้อหา — เลือกแพ็กเกจ/สรุป/ชำระเงิน (QR/ผลชำระเงินอยู่ใน PaymentQrModal แยกต่างหากแล้ว) */}
      <Card bordered={false} className="criclebox h-full tabs-mobile">
        {/* 2026-09-03: เดิม maxWidth 960 แคบเกินไปสำหรับ grid การ์ด package แบบตายตัว 4 คอลัมน์ (repeat(4,
         * 1fr)) — การ์ดแต่ละใบ min-width 160px + padding รวม 4 ใบ + gap ต้องการพื้นที่ ~830px ในคอลัมน์
         * ซ้าย (60% ของความกว้างทั้งหมด) แต่ 960 ให้พื้นที่แค่ ~510px เลยล้นขวาไปทับการ์ดสรุป/ช่องทางชำระเงิน
         * — ขยับเป็น 1400 ให้พอดีกับดีไซน์ 4 คอลัมน์แบบ admin (bangbow-admin ไม่ได้ครอบ maxWidth แคบขนาดนี้
         * เหมือนกัน จึงมีที่ว่างพอให้การ์ด 160px×4 อยู่แล้วโดยธรรมชาติ) */}
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "8px 8px 32px" }}>
          {renderForm()}
        </div>
      </Card>

      {/* 2026-09-03: modal สรุปรายการ — เปิดจากปุ่ม "ยืนยันคำสั่งซื้อ" ก่อนเข้าสู่ขั้นตอนชำระเงินจริง
       * ยกเลิก → ปิด modal นี้เฉยๆ กลับไปหน้าเลือกแพ็กเกจ / ยืนยันการชำระเงิน → ปิด modal นี้แล้วเปิด
       * modal QR (mockup) ต่อ — 2026-09-04: แบ่งเป็น 2 การ์ดแยกกล่องชัดเจน "1. รายละเอียด package"
       * (เครดิตที่ได้รับ, อายุการใช้งาน) และ "2. ข้อมูลการชำระเงิน" (ช่องทางการชำระ, ยอดชำระทั้งหมด)
       * ตามที่ขอ */}
      <Modal
        open={confirmModalOpen}
        title="สรุปรายละเอียดก่อนเติม Credit"
        centered
        width={480}
        onCancel={() => setConfirmModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModalOpen(false)}>
            ยกเลิก
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              setConfirmModalOpen(false);
              navigate(`/manage-credit/topup/checkout?packageVersionId=${selectedPackage?.version_id ?? ""}`);
            }}
          >
            ยืนยันการชำระเงิน
          </Button>,
        ]}
      >
        {/* 2026-09-04: เปลี่ยนเป็น 2 การ์ดแยกกล่องชัดเจนตามที่ขอ (มีเส้นขอบ + bo-radius คนละกล่อง ไม่ใช่
         * บล็อกเดียวคั่นด้วยเส้นแบบเดิมแล้ว) — "รายละเอียด package" (เครดิตที่ได้รับ, อายุการใช้งาน พร้อม
         * หมายเหตุว่านับจากวันที่ชำระสำเร็จ) และ "ข้อมูลการชำระเงิน" (ช่องทางการชำระ, ยอดชำระทั้งหมด) */}
        {selectedPackage && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ border: "1px solid #e6e6e6", borderRadius: 12, padding: 20 }}>
              <Text strong style={{ fontSize: 14, display: "block", marginBottom: 14 }}>
                1. รายละเอียด package
              </Text>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <Text style={{ color: "rgba(0,0,0,0.85)", fontSize: 15 }}>เครดิตที่ได้รับ</Text>
                <Text strong>{numberFormatTHB(selectedPackage.credit_amount * multiplier)} เครดิต</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Text style={{ color: "rgba(0,0,0,0.85)", fontSize: 15 }}>อายุการใช้งาน</Text>
                <div style={{ textAlign: "right" }}>
                  <Text strong style={{ display: "block" }}>{selectedPackage.expiry_days} วัน</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (หลังจากชำระเงินสำเร็จ)
                  </Text>
                </div>
              </div>
            </div>

            <div style={{ border: "1px solid #e6e6e6", borderRadius: 12, padding: 20 }}>
              <Text strong style={{ fontSize: 14, display: "block", marginBottom: 14 }}>
                2. ข้อมูลการชำระเงิน
              </Text>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <Text style={{ color: "rgba(0,0,0,0.85)", fontSize: 15 }}>ช่องทางการชำระ</Text>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <PaymentChannelIcon type={channel} style={{ fontSize: 16, color: "#1677ff" }} />
                  <Text strong>{PAYMENT_CHANNELS.find((c) => c.key === channel)?.label ?? channel}</Text>
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong style={{ fontSize: 15 }}>
                  ยอดชำระทั้งหมด
                </Text>
                <Text strong style={{ fontSize: 22, color: "#1677ff" }}>
                  {Number(selectedPackage.price_thb).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                </Text>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CreditTopupPage;
